// StudentHelpers.js

const VALID_GRADES = [
  "Pre-K",
  "Kindergarten",
  "1",
  "2",
  "3",
  "4",
  "5",
  "6",
  "7",
  "8",
  "9",
  "10",
  "11",
  "12",
];

// Common shorthands → the canonical grade values the rest of the app uses.
// (Schools often export kindergarten as "K" and grades as "1st", "2nd", …)
const GRADE_ALIASES = {
  K: "Kindergarten",
  KG: "Kindergarten",
  KINDER: "Kindergarten",
  KINDERGARTEN: "Kindergarten",
  PK: "Pre-K",
  PREK: "Pre-K",
  "PRE-K": "Pre-K",
  "PRE K": "Pre-K",
};

/**
 * Normalize a pasted/imported grade to a canonical value:
 *  - "K"/"KG"/"Kinder" → "Kindergarten", "PK"/"PreK" → "Pre-K"
 *  - "1st"/"2ND"/"3rd" → "1"/"2"/"3"
 * Unknown values pass through unchanged so validation can flag them.
 */
export const normalizeGrade = (raw) => {
  const g = (raw || "").trim();
  const key = g.toUpperCase();
  if (GRADE_ALIASES[key]) return GRADE_ALIASES[key];
  const ordinal = key.match(/^(\d{1,2})(ST|ND|RD|TH)$/);
  if (ordinal) return ordinal[1];
  return g;
};

/**
 * Validates each student object to ensure it has required fields, normalizing
 * the grade first so common shorthands (K, 1st, …) are accepted.
 * @param {Array} students
 * @returns {Array} Students with a normalized grade + validation status
 */
export const validateStudents = (students) => {
  return students.map((student) => {
    const grade = normalizeGrade(student.grade);
    // Grade is OPTIONAL — an elementary teacher shouldn't have to set one per kid.
    // Blank passes; only a NON-blank value we don't recognize is flagged.
    const gradeOk = grade === "" || VALID_GRADES.includes(grade);
    // Student ID is OPTIONAL too — rows without one get an auto-assigned internal id
    // at save time (saveStudents), so a plain list of names imports cleanly.
    const isValid = Boolean(student.studentName && gradeOk);
    return { ...student, grade, isValid };
  });
};

// ── Flexible grid import (column mapping) ────────────────────────────────────

// Fields a pasted column can be mapped to. "homeroom" is only offered in the
// admin/directory importer (a class assignment); "class"-mode imports go into the
// page's class already, so it hides that option.
export const FIELD_OPTIONS = [
  { value: "ignore",    label: "— Ignore —" },
  { value: "firstName", label: "First name" },
  { value: "lastName",  label: "Last name" },
  { value: "fullName",  label: "Full name" },
  { value: "nickname",  label: "Nickname" },
  { value: "id",        label: "Student ID" },
  { value: "grade",     label: "Grade" },
  { value: "studentEmail", label: "Student email" },
  { value: "homeroom",  label: "Homeroom / Class" },
  { value: "teacherEmail", label: "Teacher email" },
];

const HEADER_HINTS = ["first", "last", "surname", "name", "id", "number", "sis", "lasid", "grade", "level", "homeroom", "class", "teacher", "student", "email"];

// Parse pasted/CSV text into a trimmed grid. Auto-detects the delimiter (tab if any
// line has one — Sheets/Excel paste — else comma) and a likely header row.
export const parseGrid = (text) => {
  const lines = (text || "")
    .split("\n")
    .map((l) => l.replace(/\r$/, ""))
    .filter((l) => l.trim() !== "");
  if (!lines.length) return { rows: [], headerLabels: null, hasHeader: false };

  const delimiter = lines.some((l) => l.includes("\t")) ? "\t" : ",";
  let grid = lines.map((l) => l.split(delimiter).map((c) => c.trim()));

  // "Last, First" name-list special case: a comma-delimited paste where EVERY line
  // is exactly two non-numeric parts is almost certainly a list of names (not a
  // two-column CSV), and splitting it would reverse first/last. Keep each line as a
  // single Full-name column so mapRowsToStudents splits "Last, First" correctly.
  if (
    delimiter === "," &&
    grid.length > 0 &&
    grid.every((r) => r.length === 2 && r[0] && r[1] && !/^\d+$/.test(r[0]) && !/^\d+$/.test(r[1]))
  ) {
    grid = lines.map((l) => [l.trim()]);
  }

  const first = grid[0];
  const looksHeader =
    !first.every((c) => /^\d+$/.test(c)) &&
    first.some((c) => {
      const lc = c.toLowerCase();
      return HEADER_HINTS.some((h) => lc.includes(h));
    });

  return looksHeader
    ? { rows: grid.slice(1), headerLabels: first, hasHeader: true }
    : { rows: grid, headerLabels: null, hasHeader: false };
};

// Best-effort initial column→field mapping: from header labels when present,
// otherwise from the data (digits → ID, grade-like → Grade, remaining text →
// first/last name). The user can override every column via the preview dropdowns.
export const guessMapping = (headerLabels, rows) => {
  const colCount = headerLabels?.length || rows[0]?.length || 0;
  const mapping = Array(colCount).fill("ignore");
  const used = new Set();
  const assign = (i, field) => {
    if (i >= 0 && i < colCount && mapping[i] === "ignore" && !used.has(field)) {
      mapping[i] = field;
      used.add(field);
    }
  };

  if (headerLabels) {
    headerLabels.forEach((label, i) => {
      const lc = label.toLowerCase();
      // Distinguish a student's own email from the teacher-assignment email column.
      if (/email/.test(lc) && /teacher|staff|instructor/.test(lc)) assign(i, "teacherEmail");
      else if (/email/.test(lc)) assign(i, "studentEmail");
      else if (/nick|goes\s*by|preferred/.test(lc)) assign(i, "nickname");
      else if (/first/.test(lc)) assign(i, "firstName");
      else if (/last|surname/.test(lc)) assign(i, "lastName");
      else if (/full|student\s*name|^name$/.test(lc)) assign(i, "fullName");
      else if (/\bid\b|number|sis|lasid/.test(lc)) assign(i, "id");
      else if (/grade|level/.test(lc)) assign(i, "grade");
      else if (/homeroom|^hr$|class|teacher/.test(lc)) assign(i, "homeroom");
    });
  }

  // Fill remaining columns from the data.
  const sample = rows.slice(0, 8);
  const colVals = (i) => sample.map((r) => (r[i] || "").trim()).filter(Boolean);
  const leftoverText = [];
  for (let i = 0; i < colCount; i++) {
    if (mapping[i] !== "ignore") continue;
    const vals = colVals(i);
    if (!vals.length) continue;
    if (vals.every((v) => /^\d{2,}$/.test(v))) assign(i, "id");
    else if (vals.every((v) => VALID_GRADES.includes(normalizeGrade(v)))) assign(i, "grade");
    else leftoverText.push(i);
  }
  // Remaining text columns → name fields. A SINGLE leftover name column (the "just
  // paste a list of names" case) is mapped as a Full name so "First Last" / "Last,
  // First" gets split; two columns map to first + last.
  if (!used.has("fullName") && !used.has("firstName") && !used.has("lastName") && leftoverText.length === 1) {
    assign(leftoverText.shift(), "fullName");
  } else if (!used.has("fullName")) {
    if (!used.has("firstName")) assign(leftoverText.shift(), "firstName");
    if (!used.has("lastName")) assign(leftoverText.shift(), "lastName");
  }

  return mapping;
};

// Apply a column mapping to the grid → student objects, then validate. Handles a
// single "Full name" column split as "Last, First" (comma) or "First … Last".
export const mapRowsToStudents = (rows, mapping) => {
  const idxOf = (field) => mapping.indexOf(field);
  const mapped = rows.map((r) => {
    const get = (field) => {
      const i = idxOf(field);
      return i >= 0 ? (r[i] || "").trim() : "";
    };
    let firstName = get("firstName");
    let lastName = get("lastName");
    const full = get("fullName");
    if (full) {
      if (full.includes(",")) {
        const [l, f] = full.split(",");
        lastName = (l || "").trim();
        firstName = (f || "").trim();
      } else {
        const parts = full.split(/\s+/);
        firstName = parts[0] || "";
        lastName = parts.slice(1).join(" ");
      }
    }
    return {
      firstName,
      lastName,
      studentName: `${firstName} ${lastName}`.trim(),
      nickname: get("nickname"),
      id: get("id"),
      grade: get("grade"),
      email: get("studentEmail"),
      homeroomRaw: get("homeroom"),
      teacherEmailRaw: get("teacherEmail"),
    };
  });
  return validateStudents(mapped);
};
